/**
 * SignalR Helper Utility
 * Provides reliable SignalR invocation with connection checking and retry logic.
 * Use this for all SignalR invoke calls to ensure the connection is ready.
 */

/**
 * Waits for SignalR connection to be in Connected state
 * @param {Object} connection - The SignalR connection object
 * @param {Function} connectFn - Optional function to call to establish connection
 * @param {number} maxWaitMs - Maximum time to wait for connection (default: 5000ms)
 * @returns {Promise<Object|null>} - The connected connection or null if timeout
 */
export const waitForConnection = async (connection, connectFn = null, maxWaitMs = 5000) => {
    // If already connected, return immediately
    if (connection?.state === 'Connected') {
        return connection;
    }

    // Try to establish connection if function provided
    let activeConnection = connection;
    if (connectFn) {
        try {
            // connectFn (connectSignalR) may create a NEW connection object.
            // It returns the active connection so we can poll the correct reference.
            const returned = await connectFn();
            if (returned) activeConnection = returned;
        } catch (err) {
            console.warn('SignalR connect attempt failed:', err);
        }
    }

    // Wait for connection to be ready (poll every 100ms).
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitMs) {
        if (activeConnection?.state === 'Connected') {
            return activeConnection;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
    }

    return null; // Timeout
};

/**
 * Invokes a SignalR method with connection checking and retry logic
 * @param {Object} options - Options for the invoke
 * @param {Object} options.connection - The SignalR connection object
 * @param {Function} options.connectFn - Optional function to establish connection
 * @param {string} options.methodName - The hub method name to invoke
 * @param {Array} options.args - Arguments to pass to the method
 * @param {number} options.maxRetries - Maximum retry attempts (default: 2)
 * @param {number} options.maxWaitMs - Maximum wait time for connection (default: 5000ms)
 * @param {Function} options.onRetrying - Callback when retrying (optional)
 * @returns {Promise<{success: boolean, result?: any, error?: string}>}
 */
export const safeInvoke = async ({
    connection,
    connectFn = null,
    methodName,
    args = [],
    maxRetries = 2,
    maxWaitMs = 5000,
    onRetrying = null
}) => {
    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            // Wait for connection to be ready
            const readyConnection = await waitForConnection(connection, connectFn, maxWaitMs);

            if (!readyConnection || readyConnection.state !== 'Connected') {
                throw new Error('SignalR connection not available');
            }

            // Invoke the method
            const result = await readyConnection.invoke(methodName, ...args);
            return { success: true, result };

        } catch (error) {
            lastError = error;
            console.warn(`SignalR invoke attempt ${attempt + 1} failed for ${methodName}:`, error.message);

            if (attempt < maxRetries) {
                if (onRetrying) {
                    onRetrying(attempt + 1, maxRetries);
                }
                // Wait before retry with exponential backoff
                await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            }
        }
    }

    return {
        success: false,
        error: lastError?.message || 'Failed to invoke SignalR method'
    };
};

/**
 * Hook-friendly wrapper that returns an invoke function with connection management
 * Use this in components to get a reliable invoke function
 * @param {Object} connection - The SignalR connection from useAuth()
 * @param {Function} connectFn - The connectSignalR function from useAuth()
 * @returns {Function} - An invoke function that handles connection
 */
export const createSafeInvoker = (connection, connectFn) => {
    return async (methodName, ...args) => {
        return safeInvoke({
            connection,
            connectFn,
            methodName,
            args,
            maxRetries: 2,
            maxWaitMs: 5000
        });
    };
};

export default {
    waitForConnection,
    safeInvoke,
    createSafeInvoker
};
